// Global Instructions Rule Applied!
//
// Consolidated fund "Distributions" tab (docs/WATERFALL_CARRY_DESIGN.md,
// phase 5) - MIRRORS the fund full-form Distributions tab (owner ruling
// 2026-08-08: the group form uses the same UI as a normal fund wherever it
// makes sense): one FormSection, the lightweight fetch-and-render recipe,
// one row per consolidated distribution (fund_group_distributions) with its
// entities - the one distributions row per legal entity, the lead (the
// primary fund's) first. The Date link opens the LEAD distribution in the
// standard Distribution form, which runs the whole lifecycle in
// consolidated mode; "New Consolidated Distribution" opens that form
// pre-anchored to the primary fund + the group (?fund=&fund_group=), the
// way the Capital Calls tab opens a consolidated call.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_DISTRIBUTIONS } from '../../../state_manager/globals.ts'
import { fetchFundGroupDistributions } from '../services/fundGroupsService.ts'
import type {
  FundGroupDistributionEntity,
  FundGroupDistributionRow
} from '../../../state_manager/data_models/fund_group.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

interface Props {
  fundGroupId: string | null
  /** the group's primary fund - the lead row of every consolidated distribution */
  primaryFundId?: string | null
}

// "Origin $1,784,572 · Booked": an entity's part of the proceeds and where it stands
const entityText = (e: FundGroupDistributionEntity): string =>
  [e.funds__name ?? 'Entity', e.entity_gross_fmt && e.entity_gross_fmt !== '-' ? e.entity_gross_fmt : null]
    .filter(Boolean)
    .join(' ') + (e.cfg_transaction_statuses__label ? ` · ${e.cfg_transaction_statuses__label}` : '')

export function FundGroupDistributionsTab({ fundGroupId, primaryFundId }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()
  const groupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )
  const [rows, setRows] = useState<FundGroupDistributionRow[]>([])
  const [loading, setLoading] = useState(false)

  const refetch = useCallback(async () => {
    if (!fundGroupId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupDistributions(fundGroupId, token)
      if (res.error) showErrorToast(res.error)
      setRows(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundGroupId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // "New Consolidated Distribution" goes STRAIGHT into the standard
  // Distribution form: it detects ?fund_group= and creates the header + the
  // lead row on the primary fund on first save
  const handleNew = useCallback(() => {
    if (!fundGroupId || !primaryFundId) return
    openFormInTab(TABLE_DISTRIBUTIONS, null, 'New Consolidated Distribution', {
      fund: primaryFundId,
      fund_group: fundGroupId
    })
  }, [fundGroupId, primaryFundId, openFormInTab])

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'fund_group_distributions__distribution_date_fmt',
        width: 110,
        render: (v: string | null, row: FundGroupDistributionRow) =>
          row.distributions__id ? (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() =>
                openFormInTab(TABLE_DISTRIBUTIONS, row.distributions__id, row.fund_group_distributions__name || 'Distribution')
              }
            >
              {disp(v)}
            </button>
          ) : (
            <Tooltip title='The lead distribution is missing: nothing to open'>
              <span>{disp(v)}</span>
            </Tooltip>
          )
      },
      { title: 'Name', dataIndex: 'fund_group_distributions__name', render: disp },
      { title: 'Kind', dataIndex: 'cfg_distribution_kinds__label', width: 200, render: disp },
      {
        title: 'Status',
        dataIndex: 'cfg_distribution_statuses__label',
        width: 90,
        render: (v: string | null, row: FundGroupDistributionRow) =>
          v ? (
            <span>
              <Tag color={v === 'Issued' ? 'green' : 'blue'}>{v}</Tag>
              {v === 'Draft' && row.fund_group_distributions__needs_compute ? <Tag color='gold'>Needs Compute</Tag> : null}
            </span>
          ) : (
            '-'
          )
      },
      { title: 'Gross', dataIndex: 'fund_group_distributions__gross_amount_fmt', width: 130, align: 'right' as const, render: disp },
      { title: 'Lines total', dataIndex: 'prog__effective_total_fmt', width: 130, align: 'right' as const, render: disp },
      {
        // one Tag per legal entity: its part of the proceeds and its booking
        title: 'Entities',
        dataIndex: 'prog__entities',
        render: (entities: FundGroupDistributionEntity[]) =>
          entities.length === 0 ? (
            <Text type='secondary'>—</Text>
          ) : (
            <span>
              {entities.map((e) => (
                <Tag
                  key={e.distributions__id}
                  color={e.cfg_transaction_statuses__label === 'Booked' ? 'green' : e.is_lead ? 'purple' : 'blue'}
                >
                  {entityText(e)}
                </Tag>
              ))}
            </span>
          )
      }
    ],
    [openFormInTab]
  )

  if (!fundGroupId) return null

  return (
    <div className='h-full overflow-y-auto p-3 flex flex-col gap-3'>
      <FormSection
        title='Distributions'
        titleColor='blue'
        loading={loading}
        headerActions={
          <Space>
            <ExportRowsButton
              name={`${groupName} Distributions`}
              rows={rows}
              columns={[
                { title: 'Date', value: (r) => r.fund_group_distributions__distribution_date, kind: 'date' },
                { title: 'Name', value: (r) => r.fund_group_distributions__name },
                { title: 'Kind', value: (r) => r.cfg_distribution_kinds__label },
                { title: 'Status', value: (r) => r.cfg_distribution_statuses__label },
                {
                  title: 'Needs Compute',
                  value: (r) =>
                    r.cfg_distribution_statuses__label === 'Draft' && r.fund_group_distributions__needs_compute ? 'Yes' : null
                },
                { title: 'Gross', value: (r) => r.fund_group_distributions__gross_amount },
                { title: 'Lines total', value: (r) => r.prog__effective_total },
                // the Tags as text: "Origin $1,784,572 · Booked; ..."
                { title: 'Entities', value: (r) => r.prog__entities.map((e) => entityText(e)).join('; ') }
              ]}
            />
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={handleNew} disabled={!primaryFundId}>
              New Consolidated Distribution
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={() => void refetch()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Text type='secondary' className='block mb-2'>
          One distribution of the consolidated fund: a lead row on the primary fund that holds the inputs
          (the sources may be realizations of any member fund) and one row per legal entity. Open the lead
          to edit, Compute, Issue or Revert - every entity follows.
        </Text>
        {rows.length === 0 ? (
          <Text type='secondary'>No consolidated distributions yet.</Text>
        ) : (
          <Table
            size='small'
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowKey='fund_group_distributions__id'
            scroll={{ x: 'max-content' }}
          />
        )}
      </FormSection>
    </div>
  )
}

export default FundGroupDistributionsTab
